import {
  createProblem as createProblemApi,
  deleteProblem as deleteProblemApi,
  getProblem as getProblemApi,
  listProblems as listProblemsApi,
  patchProblem as patchProblemApi,
} from '@shared/api/problems';
import type {
  PagedProblems,
  Problem,
  ProblemCreateRequest,
  ProblemListQuery,
  ProblemPatchRequest,
} from '@shared/types/problem';

interface ListState {
  data: PagedProblems | null;
  loading: boolean;
  error: string | null;
}

const listState = $state<ListState>({ data: null, loading: false, error: null });
let listToken = 0;

export const problemListStore = {
  get data(): PagedProblems | null {
    return listState.data;
  },
  get loading(): boolean {
    return listState.loading;
  },
  get error(): string | null {
    return listState.error;
  },
  async refresh(query: ProblemListQuery): Promise<void> {
    const token = ++listToken;
    listState.data = null;
    listState.loading = true;
    listState.error = null;
    try {
      const data = await listProblemsApi(query);
      if (token !== listToken) return;
      listState.data = data;
    } catch (err) {
      if (token !== listToken) return;
      listState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === listToken) listState.loading = false;
    }
  },
};

interface DetailState {
  data: Problem | null;
  loading: boolean;
  error: string | null;
}

const detailState = $state<DetailState>({ data: null, loading: false, error: null });
let detailToken = 0;

export const problemDetailStore = {
  get data(): Problem | null {
    return detailState.data;
  },
  get loading(): boolean {
    return detailState.loading;
  },
  get error(): string | null {
    return detailState.error;
  },
  async load(id: string): Promise<void> {
    const token = ++detailToken;
    detailState.loading = true;
    detailState.error = null;
    try {
      const data = await getProblemApi(id);
      if (token !== detailToken) return;
      detailState.data = data;
    } catch (err) {
      if (token !== detailToken) return;
      detailState.error = err instanceof Error ? err.message : String(err);
      detailState.data = null;
    } finally {
      if (token === detailToken) detailState.loading = false;
    }
  },
  setLocal(p: Problem): void {
    detailState.data = p;
  },
  clear(): void {
    detailToken++;
    detailState.data = null;
    detailState.error = null;
    detailState.loading = false;
  },
};

/// 課題マスタを「選択肢 / id→name 解決」用に全件ロードするストア。
///
/// ProductDetailPage の「解決する課題」タブで課題名解決および候補検索に使う。
/// `/api/master/problems` は generic カタログで `listProblems` が typed を返すため
/// domain parser は通さず typed のまま保持する
/// (#475 の parse-don't-validate は業務モデルに適用する方針で、本カタログは対象外)。
/// `productOptionsStore` と同形。
interface ProblemOptionsState {
  data: Problem[];
  loading: boolean;
  error: string | null;
}

const optionsState = $state<ProblemOptionsState>({ data: [], loading: false, error: null });
let optionsToken = 0;

export const problemOptionsStore = {
  get data(): Problem[] {
    return optionsState.data;
  },
  get loading(): boolean {
    return optionsState.loading;
  },
  get error(): string | null {
    return optionsState.error;
  },
  /// 課題マスタを name 昇順で全件 (最大 500) ロードする。stale は token で破棄。
  async load(): Promise<void> {
    const token = ++optionsToken;
    optionsState.loading = true;
    optionsState.error = null;
    try {
      const res = await listProblemsApi({
        page: 1,
        page_size: 500,
        sort_by: 'name',
        sort_dir: 'asc',
      });
      if (token !== optionsToken) return;
      optionsState.data = res.items;
    } catch (err) {
      if (token !== optionsToken) return;
      optionsState.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === optionsToken) optionsState.loading = false;
    }
  },
  clear(): void {
    optionsToken++;
    optionsState.data = [];
    optionsState.loading = false;
    optionsState.error = null;
  },
};

export async function createProblem(body: ProblemCreateRequest): Promise<Problem> {
  return createProblemApi(body);
}

export async function patchProblem(id: string, body: ProblemPatchRequest): Promise<Problem> {
  const updated = await patchProblemApi(id, body);
  if (detailState.data?.id === id) detailState.data = updated;
  return updated;
}

export async function deleteProblem(id: string): Promise<void> {
  await deleteProblemApi(id);
  if (detailState.data?.id === id) detailState.data = null;
}
